<template>
  <div class="studio-panel h-full">
    <!-- Panel Header -->
    <div class="panel-header" v-if="!inDrawer">
      <span>{{ $t('Data Source') }}</span>
      <div class="flex items-center gap-2">
        <el-select
          :model-value="$i18n.locale"
          @change="(val) => $emit('change-lang', val)"
          size="small"
          class="!w-24"
        >
         <el-option value="en" label="EN" />
         <el-option value="zh-CN" label="中文" />
         <el-option value="de" label="DE" />
         <el-option value="es" label="ES" />
         <el-option value="fr" label="FR" />
         <el-option value="it" label="IT" />
         <el-option value="ja" label="日本語" />
         <el-option value="ko" label="한국어" />
         <el-option value="pt" label="PT" />
       </el-select>
        <el-button v-if="isMobile" circle text @click="$emit('close-mobile')">
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </div>

    <div class="panel-content custom-scrollbar">
      
      <!-- Premium CTA -->
      <div class="mb-4 p-3 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-100/60 relative overflow-hidden">
         <div class="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-purple-200/30 to-transparent rounded-bl-full -mr-4 -mt-4"></div>
         <div class="relative">
            <div class="flex items-center gap-2 mb-2">
               <div class="w-6 h-6 rounded-md bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                 <el-icon class="text-white" :size="14"><PriceTag /></el-icon>
               </div>
               <span class="text-xs font-bold text-indigo-800 uppercase tracking-wider">Premium</span>
            </div>
            <p class="text-xs text-gray-500 mb-2 leading-relaxed">{{ $t('Unlock all shapes, custom masks, and high-res exports.') }}</p>
            <el-button
               type="primary"
               size="small"
               class="w-full !rounded-lg"
               @click="$emit('price-click')"
            >
               {{ $t('Unlock Features') }}
            </el-button>
         </div>
      </div>

      <!-- Quick Analyze -->
      <div class="panel-section" data-tour="input">
        <div class="panel-section-title">
          <el-icon class="mr-1"><EditPen /></el-icon> {{ $t('Quick Paste') }}
        </div>
        <el-input 
          :model-value="textToAnalyze"
          :placeholder="$t('Paste text to analyze...')"
          type="textarea"
          :rows="3"
          resize="none"
          class="mb-2"
          @input="$emit('update:textToAnalyze', $event)"
        ></el-input>
        <el-button 
          plain 
          size="small" 
          class="w-full" 
          @click="$emit('analyze-text')"
          :loading="loading"
        >
          {{ $t('Analyze') }}
        </el-button>
      </div>

      <!-- Manual Input -->
      <div class="panel-section">
        <div class="panel-section-title">
           <el-icon class="mr-1"><Plus /></el-icon> {{ $t('Manual Entry') }}
        </div>
        <div class="flex gap-2 mb-2">
           <el-input v-model="inputKey" :placeholder="$t('Word')" size="small" />
           <el-input v-model="inputValue" type="number" :placeholder="$t('Val')" size="small" style="width: 80px;" />
        </div>
        <el-button type="primary" plain size="small" class="w-full" @click="addLocalData">
           {{ $t('Add Entry') }}
        </el-button>
      </div>

      <!-- Data List -->
      <div class="panel-section flex-1">
         <div class="panel-section-title flex justify-between">
           <span>{{ $t('Dataset') }} ({{ processedChartData.length }})</span>
           <el-button link type="danger" size="small" class="!h-auto !p-0 text-xs" @click="$emit('clear-data')">{{ $t('Clear') }}</el-button>
         </div>
         
         <div class="bg-gray-50 rounded border border-gray-100 overflow-hidden min-h-[200px] flex flex-col">
            <template v-if="processedChartData.length > 0">
              <el-table
                :data="processedChartData" 
                :key="tableKey"
                class="w-full !bg-transparent"
                :show-header="false"
                size="small"
                height="200"
              >
                <el-table-column :label="$t('Name')">
                  <template #default="{ row, $index }">
                    <el-input 
                      v-model="row.name" 
                      size="small" 
                      @change="handleDataChange($index, 'name', row.name)"
                      class="!border-0 !shadow-none hover:!border-gray-200 focus-within:!border-indigo-500"
                    />
                  </template>
                </el-table-column>
                <el-table-column :label="$t('Value')" width="110" align="right">
                  <template #default="{ row, $index }">
                    <el-input-number 
                      v-model="row.value" 
                      size="small"
                      :min="1"
                      controls-position="right"
                      @change="handleDataChange($index, 'value', row.value)"
                      class="!w-full"
                    />
                  </template>
                </el-table-column>
                <el-table-column width="45" align="center">
                  <template #default="scope">
                    <el-button 
                      link 
                      type="danger" 
                      size="small"
                      @click.stop="$emit('delete-data', scope.$index)"
                      class="!p-1"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </template>
                </el-table-column>              
              </el-table>
            </template>
            <div v-else class="empty-state" style="min-height:180px">
               <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                 <el-icon class="text-gray-300" :size="24"><FolderOpened /></el-icon>
               </div>
               <div class="empty-state-title">{{ $t('No data yet') }}</div>
               <div class="empty-state-desc">{{ $t('No data. Add words locally or paste text above.') }}</div>
            </div>
         </div>
      </div>
    
      <!-- Footer -->
      <div class="mt-4 pt-4 border-t border-gray-100 text-center">
         <div class="flex justify-center gap-4 text-xs text-gray-400">
           <span class="cursor-pointer hover:text-indigo-500 transition-colors" @click="$emit('open-service')">{{ $t('Support') }}</span>
           <a href="./blog/" target="_blank" class="hover:text-indigo-500 transition-colors">{{ $t('Blog') }}</a>
         </div>
         <div class="mt-2 text-xs text-gray-400">
           📧 <a href="mailto:emperinter@outlook.com" class="hover:text-indigo-500 transition-colors">emperinter@outlook.com</a>
         </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ElButton, ElIcon, ElInput, ElInputNumber, ElTable, ElTableColumn, ElSelect, ElOption } from 'element-plus';
import { Close, Delete, PriceTag, Plus, EditPen, FolderOpened } from '@element-plus/icons-vue';

export default {
  name: 'LeftSidebar',
  components: { ElButton, ElIcon, ElInput, ElInputNumber, ElTable, ElTableColumn, ElSelect, ElOption, Close, Delete, PriceTag, Plus, EditPen, FolderOpened },
  props: {
    isMobile: Boolean,
    isSubscribed: Boolean,
    textToAnalyze: String,
    processedChartData: Array,
    loading: Boolean,
    tableKey: Number,
    inDrawer: Boolean
  },
  emits: ['update:textToAnalyze', 'analyze-text', 'add-data', 'delete-data', 'update-data', 'change-lang', 'price-click', 'open-service', 'close-mobile', 'clear-data'],
  data() {
    return {
      inputKey: '',
      inputValue: ''
    };
  },
  methods: {
    addLocalData() {
      if (this.inputKey && this.inputValue) {
        this.$emit('add-data', { key: this.inputKey, value: this.inputValue });
        this.inputKey = '';
        this.inputValue = '';
      }
    },
    handleDataChange(index, field, value) {
      this.$emit('update-data', { index, field, value });
    }
  }
}
</script>
